import { HIDDEN_PHONE_HE, phoneDigitsOf } from '@nw/shared';

/** Display helpers for lane B2a screens (Hebrew, Asia/Jerusalem). */

const TZ = 'Asia/Jerusalem';

function parts(d: Date) {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: p.year ?? '', m: p.month ?? '', d: p.day ?? '', hh: p.hour === '24' ? '00' : (p.hour ?? ''), mm: p.minute ?? '' };
}

function dayKey(d: Date): string {
  const p = parts(d);
  return `${p.y}-${p.m}-${p.d}`;
}

/** "14:02" today, "אתמול 16:02", else "08.10". */
export function shortTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = parts(d);
  const now = new Date();
  if (dayKey(d) === dayKey(now)) return `${p.hh}:${p.mm}`;
  if (dayKey(d) === dayKey(new Date(now.getTime() - 86_400_000))) return `אתמול ${p.hh}:${p.mm}`;
  return `${p.d}.${p.m}`;
}

/** "היום 10:21", "אתמול", "08.10.2026" */
export function lastContact(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = parts(d);
  const now = new Date();
  if (dayKey(d) === dayKey(now)) return `היום ${p.hh}:${p.mm}`;
  if (dayKey(d) === dayKey(new Date(now.getTime() - 86_400_000))) return 'אתמול';
  return `${p.d}.${p.m}.${p.y}`;
}

/** "12.10.2026, 09:14" */
export function fullDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const p = parts(new Date(iso));
  return `${p.d}.${p.m}.${p.y}, ${p.hh}:${p.mm}`;
}

/** "לפני 40 שניות", "לפני 5 דקות", "לפני 3 שעות", else a short time. */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'אף פעם';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 10) return 'עכשיו';
  if (s < 60) return `לפני ${s} שניות`;
  const m = Math.round(s / 60);
  if (m < 60) return m === 1 ? 'לפני דקה' : `לפני ${m} דקות`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? 'לפני שעה' : `לפני ${h} שעות`;
  return shortTime(iso);
}

/** "972525550142" -> "+972 52-555-0142"; other countries: "+<digits>". */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '-';
  // a WhatsApp privacy id (@lid), a group id (@g.us)… is never a phone number
  if (raw.includes('@')) {
    const wa = phoneDigitsOf(raw);
    return wa ? formatPhone(wa) : '-';
  }
  const d = raw.replace(/\D/g, '');
  if (!d) return raw;
  if (d.startsWith('972') && d.length === 12) return `+972 ${d.slice(3, 5)}-${d.slice(5, 8)}-${d.slice(8)}`;
  if (d.startsWith('05') && d.length === 10) return `+972 ${d.slice(1, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `+${d}`;
}

/** formatPhone, but "מספר מוסתר" instead of "—" when there is nothing to show */
export function phoneOrHidden(raw: string | null | undefined): string {
  const f = formatPhone(raw);
  return f === '-' ? HIDDEN_PHONE_HE : f;
}

export const digits = (s: string | null | undefined) => String(s ?? '').replace(/\D/g, '');

/** Same check the server does for the logout confirmation: the number in any format (with or without 972 / leading 0). */
export function samePhone(typed: string, phone: string | null): boolean {
  const a = digits(typed);
  const b = digits(phone);
  if (!a || !b) return false;
  const norm = (x: string) => (x.startsWith('972') ? x.slice(3) : x.startsWith('0') ? x.slice(1) : x);
  return norm(a) === norm(b);
}

/** Two-letter initials for avatars. */
export function initials(name: string | null | undefined): string {
  const w = String(name ?? '')
    .replace(/[^A-Za-z֐-׿\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!w.length) return '?';
  return (w.length > 1 ? (w[0]?.charAt(0) ?? '') + (w[1]?.charAt(0) ?? '') : (w[0] ?? '').slice(0, 2)).toUpperCase();
}

export function hashIdx(s: string): number {
  let x = 0;
  for (let i = 0; i < s.length; i++) x = (x * 33 + s.charCodeAt(i)) % 997;
  return (x % 6) + 1;
}
